import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import ExifReader from "exifreader";
import { format, parse } from "date-fns";
import Row from "react-bootstrap/Row";
import InputGroup from "react-bootstrap/InputGroup";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import ProgressBar from "react-bootstrap/ProgressBar";
import has from "lodash/has";
import get from "lodash/get";
import Button from "react-bootstrap/esm/Button";
import Modal from "react-bootstrap/Modal";
import UploadPreview from "./UploadPreview";
import { UploadsMutation, hash } from "./gql/UploadsMutation";
import {
  SingleUploadMutation,
  hash as hash2,
} from "./gql/SingleUploadMutation";
import { PhotoUploadMutation, hash as hash3 } from "./gql/PhotoUploadMutation";
import {
  UploadConfirmMutation,
  hash as hash4,
} from "./gql/UploadConfirmMutation";
import { UpdatePhotoGeom, hash as hash5 } from "./gql/UpdatePhotoGeomMutation";
import {
  FailedUploadMutation,
  hash as hash6,
} from "./gql/FailedUploadMutation";
import ProjectSelect from "./ProjectSelect";
import DeviceSelect from "./DeviceSelect";

const dateTimeFormatter = (date, time, dateTime, dateTimeFormat) => {
  let outDate = date;

  if (dateTime) {
    const getDate = parse(dateTime, dateTimeFormat, new Date());
    outDate = format(getDate, "yyyy-MM-dd HH:mm:ss");
    //console.info(outDate);
    return outDate;
  }
  // if there is no date, return null

  if (date && time) {
    const dateTime = date.toString() + " " + time.toString();
    const checkDate = parse(dateTime, dateTimeFormat, new Date());
    outDate = format(checkDate, "yyyy-MM-dd HH:mm:ss");
    return outDate;
  }

  return null;
};

const UploadForm = ({ user }) => {
  const [projectName, setProjectName] = useState();
  const [deviceName, setDeviceName] = useState();

  //device fields
  const [latField, setLatField] = useState();
  const [lonField, setLonField] = useState();
  const [altField, setAltField] = useState();
  const [headField, setHeadField] = useState();
  const [imageryType, setImageryType] = useState();
  const [dateTimeField, setDateTimeField] = useState();
  const [dateField, setDateField] = useState();
  const [timeField, setTimeField] = useState();
  const [dateTimeFormat, setDateTimeFormat] = useState();
  const [fileFormat, setFileFormat] = useState();

  //file upload properties
  const [files, setFiles] = useState([]);
  const [numFiles, setNumFiles] = useState(0);
  const [currentFile, setCurrentFile] = useState(0);
  const [uploadError, setUploadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState();

  // editing
  const [remove, setRemove] = useState(new Set([]));

  const [newUpload] = useMutation(UploadsMutation, {
    context: { headers: { "X-Trust-Key": hash } },
  });
  const [singleUpload] = useMutation(SingleUploadMutation, {
    context: { headers: { "X-Trust-Key": hash2 } },
  });
  const [uploadPhoto] = useMutation(PhotoUploadMutation, {
    context: { headers: { "X-Trust-Key": hash3 } },
  });
  const [confirmUpload] = useMutation(UploadConfirmMutation, {
    context: { headers: { "X-Trust-Key": hash4 } },
  });
  const [updateGeom] = useMutation(UpdatePhotoGeom, {
    context: { headers: { "X-Trust-Key": hash5 } },
  });
  const [fileError] = useMutation(FailedUploadMutation, {
    context: { headers: { "X-Trust-Key": hash6 } },
  });

  const handleFileError = async (uploadId, baseName, message, path) => {
    try {
      await fileError({
        variables: {
          uploadId: uploadId,
          filename: baseName,
          problem: message,
          relativePath: path,
        },
      });
      return;
    } catch (error) {
      console.error(error);
    }
  };

  const handleUploadError = async (uploadId, numFiles, totalSize, message) => {
    try {
      await confirmUpload({
        variables: {
          id: uploadId,
          count: numFiles,
          size: totalSize,
          status: message,
        },
      });
      setUploading(false);

      return;
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setUploading(true);
    setUploadMessage("Starting");
    let uploadId;
    try {
      const startUpload = await newUpload({
        variables: {
          userId: user.appId,
          projectName: projectName,
          zipFileName: "N/A",
          zipFileSize: 0,
          bulk: false,
        },
      });

      const output = startUpload?.data?.createUpload?.upload?.id;

      uploadId = output;
    } catch (error) {
      setUploadMessage("Problem Starting Upload");
      setUploadError(true);
      setUploading(false);
      return;
    }

    var fileCount = 0;
    var totalSize = 0;
    setNumFiles(files.length - remove.size);

    if (uploadId) {
      for (let i = 0; i < files.length; i++) {
        if (remove.has(i)) {
          continue;
        }
        setCurrentFile(i);
        if (
          files[i].type === fileFormat ||
          files[i].name.substr(
            files[i].name.split("\\").pop().split("/").pop().lastIndexOf(".") +
              1
          ) === "36P"
        ) {
          let uploadUrl;
          const baseName = files[i].name.split("\\").pop().split("/").pop();

          const fileExt =
            baseName.substr(baseName.lastIndexOf(".") + 1).toUpperCase() ===
            "36P"
              ? "JPG"
              : baseName.substr(baseName.lastIndexOf(".") + 1);

          const webName =
            Date.now().toString(36) +
            Math.floor(
              Math.pow(10, 12) + Math.random() * 9 * Math.pow(10, 12)
            ).toString(36) +
            "." +
            fileExt;

          const keyName = `uploads/${encodeURI(
            projectName
          )}/${uploadId}/${webName}`;

          try {
            const uploadInfo = await singleUpload({
              variables: {
                uploadId: uploadId,
                projectName: projectName,
                fileName: webName,
              },
            });

            uploadUrl = uploadInfo?.data?.singleUpload?.url;
          } catch (error) {
            console.error(error);
            setUploadMessage("Can not start upload");
            setUploadError(true);
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
          }

          const buffer = await files[i].arrayBuffer();
          const tags = await ExifReader.load(buffer, {
            expanded: true,
            includeUnknown: true,
            async: true,
          });

          if (!tags) {
            setUploadMessage("Missing EXIF Data");
            await handleFileError(
              uploadId,
              baseName,
              "Missing EXIF Data",
              files[i].webkitRelativePath
            );
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
            continue;
          }
          // check if file has GPS Information
          if (!has(tags, latField) || !has(tags, lonField)) {
            setUploadMessage("Missing Geotagging Data");
            await handleFileError(
              uploadId,
              baseName,
              "Missing Geotagging Data",
              files[i].webkitRelativePath
            );
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
            continue;
          }

          if (altField) {
            if (!has(tags, altField)) {
              setUploadMessage("Missing Altitude Field");
              await handleFileError(
                uploadId,
                baseName,
                "Imagery Missing Altitude Data",
                files[i].webkitRelativePath
              );
              if (files.length === 1) {
                await confirmUpload({
                  variables: {
                    id: uploadId,
                    count: "1",
                    size: totalSize,
                    status: "ERROR",
                  },
                });
              }
              continue;
            }
          }

          if (headField) {
            if (!has(tags, headField)) {
              setUploadMessage("Missing Heading Field");
              await handleFileError(
                uploadId,
                baseName,
                "Imagery Missing Heading Data",
                files[i].webkitRelativePath
              );
              if (files.length === 1) {
                await confirmUpload({
                  variables: {
                    id: uploadId,
                    count: "1",
                    size: totalSize,
                    status: "ERROR",
                  },
                });
              }
              continue;
            }
          }

          let photoDateTime;
          if (dateTimeField !== "FILENAME") {
            if (
              (!has(tags, timeField) || !has(tags, dateField)) &&
              !has(tags, dateTimeField)
            ) {
              setUploadMessage("Missing Date Time");
              await handleFileError(
                uploadId,
                baseName,
                "Missing Date Time",
                files[i].webkitRelativePath
              );
              if (files.length === 1) {
                await confirmUpload({
                  variables: {
                    id: uploadId,
                    count: "1",
                    size: totalSize,
                    status: "ERROR",
                  },
                });
              }
              continue;
            }

            photoDateTime = dateTimeFormatter(
              get(tags, dateField) || null,
              get(tags, timeField) || null,
              get(tags, dateTimeField),
              dateTimeFormat
            );
          } else {
            const dateTimeFileName = baseName
              .replace(/\D/g, "")
              .substring(0, 14);
            photoDateTime = dateTimeFormatter(
              null,
              null,
              dateTimeFileName,
              dateTimeFormat
            );
          }
          if (!photoDateTime) {
            setUploadMessage(
              "Problems with the format of the file or exif time stamps"
            );
            await handleFileError(
              uploadId,
              baseName,
              "Problems with the format of the file or exif time stamps",
              files[i].webkitRelativePath
            );
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
            continue;
          }

          let gpsLatitude = get(tags, latField);
          let gpsLongitude = get(tags, lonField);
          let gpsAltitude = get(tags, altField) || null;
          let gpsHeading = get(tags, headField) || null;

          if (
            isNaN(gpsLatitude) ||
            isNaN(gpsLongitude) ||
            !gpsLatitude ||
            !gpsLongitude
          ) {
            await handleFileError(
              uploadId,
              baseName,
              "Lat/Long Fields Exist but value is not set.",
              files[i].webkitRelativePath
            );
            setUploadMessage("Unable to Upload File. Moving to Next Image.");
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
            continue;
          }

          gpsLatitude = parseFloat(gpsLatitude.toFixed(6));
          gpsLongitude = parseFloat(gpsLongitude.toFixed(6));
          gpsAltitude = gpsAltitude
            ? parseFloat(gpsAltitude.toFixed(6))
            : gpsAltitude;
          gpsHeading = gpsHeading ? parseFloat(gpsHeading) : gpsHeading;

          const url = `${process.env.REACT_APP_IMAGE_SITE}/${keyName}`;

          let success = false;
          let uploadCount = 1;
          while (!success) {
            try {
              if (uploadCount > 1) {
                setUploadMessage(`Upload attempt ${uploadCount}...`);
              }
              if (uploadUrl) {
                await fetch(uploadUrl, {
                  method: "PUT",
                  headers: {
                    "Content-Type":
                      files[i].type !== "" ? files[i].type : "image/jpg",
                    "Cache-Control": "public",
                    "Content-Length": files[i].size,
                  },
                  body: files[i],
                });
                setUploadMessage();
                success = true;
              } else {
                await handleFileError(
                  uploadId,
                  baseName,
                  "Unable to get upload address for file",
                  files[i].webkitRelativePath
                );
                setUploadMessage(
                  "Unable to Upload File. Moving to Next Image."
                );
                if (files.length) {
                  await confirmUpload({
                    variables: {
                      id: uploadId,
                      count: "1",
                      size: totalSize,
                      status: "ERROR",
                    },
                  });
                }

                continue;
              }
            } catch (error) {
              console.error(error);
              uploadCount += 1;
              success = false;
            }
          }

          try {
            await uploadPhoto({
              variables: {
                uploadId: uploadId,
                url: url,
                lat: gpsLatitude,
                lon: gpsLongitude,
                alt: gpsAltitude,
                head: gpsHeading,
                imgType: imageryType,
                originalFileName: baseName,
                photoDateTime: photoDateTime,
                projectName: projectName,
                webname: webName,
              },
            });
          } catch (error) {
            setUploadMessage("Could not finalize image upload.");
            await handleFileError(
              uploadId,
              baseName,
              "Upload Update Error",
              files[i].webkitRelativePath
            );
            if (files.length === 1) {
              await confirmUpload({
                variables: {
                  id: uploadId,
                  count: "1",
                  size: totalSize,
                  status: "ERROR",
                },
              });
            }
          }

          totalSize += files[i].size;
          fileCount += 1;
          continue;
        }
      }

      try {
        await updateGeom({
          variables: {
            uploadId: uploadId,
          },
        });
      } catch (error) {
        setUploadMessage("Could not set geometry.");
        await handleUploadError(uploadId, fileCount, totalSize, "GEOM ERROR");
      }

      try {
        await confirmUpload({
          variables: {
            id: uploadId,
            count: fileCount.toString(),
            size: totalSize,
            status: "COMPLETE",
          },
        });
      } catch (error) {
        console.error(error);
      }
    }
    setUploading(false);
    setUploadError(false);
    setUploadMessage();
    setProjectName();
    setDeviceName();
    setLatField();
    setLonField();
    setAltField();
    setHeadField();
    setDateField();
    setTimeField();
    setDateTimeField();
    setDateTimeFormat();
    setImageryType();
    setFiles([]);
    setRemove(new Set());
    e.target.reset();
  };

  return (
    <>
      <Modal show={uploading} centered>
        <Modal.Header>
          <Modal.Title>Uploading {files?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          File {currentFile} of {numFiles}
          <ProgressBar
            now={numFiles > 0 && Math.floor((currentFile / numFiles) * 100)}
            label={
              numFiles > 0 && `${Math.floor((currentFile / numFiles) * 100)}%`
            }
          ></ProgressBar>
          {uploadMessage && <p>{uploadMessage}</p>}
        </Modal.Body>
        <Modal.Footer></Modal.Footer>
      </Modal>
      <Card>
        <Card.Header>
          <Card.Title>Upload New Files</Card.Title>
        </Card.Header>
        <Card.Body>
          {uploadError && <Alert>There was an error with the upload.</Alert>}
          <Form id="upload-form" onSubmit={handleSubmit}>
            <Row className="p-2">
              <ProjectSelect
                projectName={projectName}
                setProjectName={setProjectName}
              />
            </Row>
            <Row className="p-2">
              <DeviceSelect
                deviceName={deviceName}
                setDeviceName={setDeviceName}
                setDateField={setDateField}
                setDateTimeField={setDateTimeField}
                setTimeField={setTimeField}
                setLatField={setLatField}
                setLonField={setLonField}
                setAltField={setAltField}
                setHeadField={setHeadField}
                setImageryType={setImageryType}
                setDateTimeFormat={setDateTimeFormat}
                setFileFormat={setFileFormat}
              />
            </Row>

            <Row className="p-2">
              <Form.Group>
                <Form.Label>Select File to Upload</Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    type="file"
                    multiple
                    accept="image/*, .36p, .360, .jpg, .jpeg, .png"
                    onChange={(e) => setFiles(e.target.files)}
                  />
                  <UploadPreview
                    images={files}
                    latField={latField}
                    lonField={lonField}
                    remove={remove}
                    setRemove={setRemove}
                  />
                  <Button
                    size="sm"
                    disabled={uploading || files.length < 1}
                    type="submit"
                    form="upload-form"
                    style={{ color: "white", backgroundColor: "orange" }}
                  >
                    Upload
                  </Button>
                </InputGroup>
              </Form.Group>
            </Row>

            <Row className="p-2">
              <Form.Group>
                <Form.Label>Select Folder to Upload</Form.Label>
                <InputGroup>
                  <Form.Control
                    size="sm"
                    multiple
                    webkitdirectory=""
                    mozdirectory=""
                    directory=""
                    type="file"
                    onChange={(e) => setFiles(e.target.files)}
                  />
                  <UploadPreview
                    images={files}
                    latField={latField}
                    lonField={lonField}
                    remove={remove}
                    setRemove={setRemove}
                  />
                  <Button
                    size="sm"
                    disabled={uploading || files.length < 1}
                    type="submit"
                    form="upload-form"
                    style={{ color: "white", backgroundColor: "orange" }}
                  >
                    Upload
                  </Button>
                </InputGroup>
              </Form.Group>
            </Row>

            <Row className="d-flex justify-content-center align-items-center pt-2">
              <InputGroup
                size="sm"
                className="d-flex flex-row align-self-center justify-content-center"
              >
                <InputGroup.Text size="sm">
                  Upload {files.length - (remove.size || 0)} out of{" "}
                  {files.length}
                </InputGroup.Text>
                <Button
                  size="sm"
                  disabled={uploading || remove.size < 1 || files.length < 1}
                  onClick={() => {
                    setRemove(new Set());
                  }}
                  variant="danger"
                >
                  Reset
                </Button>
              </InputGroup>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};

export default UploadForm;
